import React, { useState } from 'react';
import styled from 'styled-components';
import { orderService } from '../../../data';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, getWeek } from 'date-fns';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import Select, { components } from 'react-select';

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 15px;
    max-width: 100%;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const Label = styled.label`
  color: #4A3C31;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
    display: block;
  }
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #C5A572;
  border-radius: 4px;

  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.9rem;
    width: 100%;
    box-sizing: border-box;
  }
`;

const Button = styled.button`
  background: #A68B5B;
  color: #fff;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: #4A3C31; }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    width: 100%;
  }
`;

const DownloadButton = styled.button`
  background: #A68B5B;
  color: #fff;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 1.5rem;
  margin-right: 1rem;
  &:hover { background: #4A3C31; }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    margin-right: 0;
    width: 100%;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #4A3C31;

  @media (max-width: 768px) {
    padding: 1.5rem;
    font-size: 0.9rem;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  padding: 1rem;
  background: #ffebee;
  border-radius: 4px;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
  }
`;

const ProductFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }
`;

const GroupSelect = styled.select`
  min-width: 120px;
  padding: 0.4rem;
  border: 1px solid #C5A572;
  border-radius: 4px;
  font-size: 1rem;

  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
    padding: 0.75rem;
    font-size: 0.9rem;
    box-sizing: border-box;
  }
`;

const customSelectStyles = {
  container: (provided) => ({ 
    ...provided, 
    minWidth: 240,
    '@media (max-width: 768px)': {
      minWidth: '100%',
      width: '100%'
    }
  }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
  control: (provided, state) => ({
    ...provided,
    '@media (max-width: 768px)': {
      minHeight: '44px',
      fontSize: '0.9rem'
    }
  }),
  option: (provided, state) => ({
    ...provided,
    '@media (max-width: 768px)': {
      fontSize: '0.9rem',
      padding: '8px 12px'
    }
  })
};

// Custom Option with checkbox for react-select
const CheckboxOption = (props) => {
  return (
    <components.Option {...props}>
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => {}}
        style={{ marginRight: 8 }}
      />
      <label>{props.label}</label>
    </components.Option>
  );
};

const SalesHistoryTab = ({ market, customerId, storeName }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [groupBy, setGroupBy] = useState('week'); // 'week' | 'month' | 'year'

  const localDate = new Date();
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(localDate.getDate()).padStart(2, '0');
  const today = `${year}-${month}-${day}`;

  // Get all unique product names from history
  const allProductNames = React.useMemo(() => {
    if (!history || !history.days) return [];
    const set = new Set();
    history.days.forEach(day => {
      day.products.forEach(product => set.add(product.product_name));
    });
    return Array.from(set).sort();
  }, [history]);

  const productOptions = React.useMemo(
    () => allProductNames.map(name => ({ value: name, label: name })),
    [allProductNames]
  );

  // Handle product filter change
  const handleProductFilterChange = (selected) => {
    setSelectedProducts(selected || []);
  };

  // Filter days/products by selected products
  const filteredDays = React.useMemo(() => {
    if (!history || !history.days) return [];
    if (!selectedProducts.length) return history.days; // Show all if none selected
    const selectedNames = selectedProducts.map(opt => opt.value);
    return history.days.map(day => ({
      ...day,
      products: day.products.filter(product => selectedNames.includes(product.product_name))
    })).filter(day => day.products.length > 0);
  }, [history, selectedProducts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customerId || !market || !startDate || !endDate) {
      console.error('Missing required params:', { customerId, market, startDate, endDate });
      setError('Please select a date range and ensure market is set.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      console.log('[SalesHistoryTab] Fetching history with:', { customerId, market, startDate, endDate });
      const data = await orderService.getOrderHistory(customerId, market, startDate, endDate);
      setHistory(data);
    } catch (err) {
      setError('Failed to load order history');
      console.error('Error loading order history:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group data by selected period (filtered)
  const getGroupedData = () => {
    if (!filteredDays.length) return [];
    const groupMap = {};
    filteredDays.forEach(day => {
      const dateObj = new Date(day.date);
      let groupKey = '';
      let dateRange = '';
      if (groupBy === 'week') {
        const weekStart = startOfWeek(dateObj, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(dateObj, { weekStartsOn: 1 });
        dateRange = `${format(weekStart, 'M/d/yy')} to ${format(weekEnd, 'M/d/yy')}`;
        groupKey = dateRange;
        // We'll assign week numbers after collecting all groups
      } else if (groupBy === 'month') {
        const monthStart = startOfMonth(dateObj);
        const monthEnd = endOfMonth(dateObj);
        dateRange = `${format(monthStart, 'M/d/yy')} to ${format(monthEnd, 'M/d/yy')}`;
        groupKey = dateRange;
        // We'll assign month numbers after collecting all groups
      } else if (groupBy === 'year') {
        const yearStart = startOfYear(dateObj);
        const yearEnd = endOfYear(dateObj);
        dateRange = `${format(yearStart, 'M/d/yy')} to ${format(yearEnd, 'M/d/yy')}`;
        groupKey = dateRange;
        // We'll assign year numbers after collecting all groups
      }
      if (!groupMap[groupKey]) {
        groupMap[groupKey] = { group: groupKey, qtyOrdered: 0, qtyDelivered: 0, dateRange };
      }
      day.products.forEach(product => {
        groupMap[groupKey].qtyOrdered += product.qty_ordered;
        groupMap[groupKey].qtyDelivered += product.qty_delivered;
      });
    });
    // Assign week/month/year numbers as labels
    const groups = Object.values(groupMap);
    if (groupBy === 'week') {
      groups.forEach((g) => {
        const startDateStr = g.dateRange.split(' to ')[0];
        const startDate = new Date(startDateStr);
        const weekNum = getWeek(startDate, { weekStartsOn: 1 });
        g.label = `Wk ${weekNum}`;
      });
    } else if (groupBy === 'month') {
      groups.forEach((g) => {
        // Parse the start date from the dateRange string
        const startDateStr = g.dateRange.split(' to ')[0];
        const startDate = new Date(startDateStr);
        g.label = format(startDate, 'MMM yyyy');
      });
    } else if (groupBy === 'year') {
      groups.forEach((g) => {
        const startDateStr = g.dateRange.split(' to ')[0];
        const startDate = new Date(startDateStr);
        g.label = format(startDate, 'yyyy');
      });
    }
    return groups;
  };

  const groupedData = getGroupedData();

  // Custom tooltip to show date range
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{ background: 'white', border: '1px solid #C5A572', borderRadius: 6, padding: 10, fontSize: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{data.label}</div>
          <div style={{ color: '#4A3C31', marginBottom: 4 }}>{data.dateRange}</div>
          <div style={{ color: '#A68B5B' }}>Qty Ordered: <b>{data.qtyOrdered}</b></div>
          <div style={{ color: '#4A3C31' }}>Qty Delivered: <b>{data.qtyDelivered}</b></div>
        </div>
      );
    }
    return null;
  };

  // Dynamically set XAxis label angle and textAnchor
  const xAxisAngle = groupedData.length <= 12? 0 : -90;
  const xAxisTextAnchor = groupedData.length <= 12 ? 'middle' : 'end';

  // CSV Download logic (filtered, grouped by day for detail)
  const handleDownloadCSV = () => {
    if (!filteredDays.length) return;
    const rows = [];
    filteredDays.forEach(day => {
      day.products.forEach(product => {
        rows.push({
          storeName: storeName || '',
          invoiceDate: day.date,
          productName: product.product_name,
          qtyOrdered: product.qty_ordered,
          qtyDelivered: product.qty_delivered,
          unitPrice: product.unit_price
        });
      });
    });
    const csvHeader = 'Store Name,Invoice Date,Product Name,QtyOrdered,QtyDelivered,Unit Price';
    const csvRows = rows.map(row => [
      '"' + (row.storeName || '') + '"',
      '"' + (row.invoiceDate || '') + '"',
      '"' + (row.productName || '') + '"',
      row.qtyOrdered,
      row.qtyDelivered,
      row.unitPrice
    ].join(','));
    const csvContent = [csvHeader, ...csvRows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_history_${startDate}_to_${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Container>      
      <form onSubmit={handleSubmit}>
        <FormRow>
          <Label>Start Date</Label>
          <Input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            required
            max={today}
          />
          <Label>End Date</Label>
          <Input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            required
            min={startDate}
            max={today}
          />
          <Button type="submit">Show History</Button>
        </FormRow>
      </form>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading ? (
        <LoadingMessage>Loading order history...</LoadingMessage>
      ) : history && (
        <>
          {/* Product Filter and Group By */}
          <ProductFilterRow>
            <Label>Product Filter</Label>
            <div style={{ 
              minWidth: 240,
              '@media (max-width: 768px)': {
                minWidth: '100%',
                width: '100%'
              }
            }}>
              <Select
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                options={productOptions}
                value={selectedProducts}
                onChange={handleProductFilterChange}
                styles={customSelectStyles}
                placeholder="Select products..."
                components={{ Option: CheckboxOption }}
              />
            </div>
            <Label style={{ 
              marginLeft: 24,
              '@media (max-width: 768px)': {
                marginLeft: 0,
                marginTop: '0.5rem'
              }
            }}>Group By</Label>
            <GroupSelect value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </GroupSelect>
          </ProductFilterRow>
          {/* Bar Chart for grouped data */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            marginBottom: 16,
            '@media (max-width: 768px)': {
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: '0.75rem'
            }
          }}>
            <div style={{ 
              fontWeight: 500, 
              fontSize: 18,
              '@media (max-width: 768px)': {
                fontSize: 16,
                textAlign: 'center'
              }
            }}></div>
            <DownloadButton onClick={handleDownloadCSV}>Download CSV</DownloadButton>
          </div>
          <div style={{ 
            width: '100%', 
            height: 350, 
            marginBottom: 32,
            '@media (max-width: 768px)': {
              height: 300,
              marginBottom: 20
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={groupedData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12 }}
                  interval={0}
                  angle={xAxisAngle}
                  textAnchor={xAxisTextAnchor}
                  height={xAxisAngle === 0 ? 40 : 100}
                />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend verticalAlign="top" align="center" wrapperStyle={{ marginBottom: 12 }} />
                <Bar dataKey="qtyOrdered" fill="#A68B5B" name="Qty Ordered" />
                <Bar dataKey="qtyDelivered" fill="#4A3C31" name="Qty Delivered" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Container>
  );
};

export default SalesHistoryTab; 